import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  FileText,
  Layers,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  createContentItem,
  deleteContentItem,
  deleteContentMedia,
  getAdminPageContent,
  updateContentActive,
  updateContentOrder,
  updateContentValue,
  uploadContentMedia,
} from "../../../api/contentAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { useTranslation } from "react-i18next";
import EditableContentRow from "./EditableContentRow";
import {
  flattenContent,
  formatFieldTitle,
  getSectionKey,
  languages,
  normalizeLanguage,
  resolveMediaUrl,
  toSectionTitle,
} from "./contentManagerUtils";

const ContentManager = ({ t, selectedPage = "home", contentPages = [], onPageChange }) => {
  const { notify } = useNotification();
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(() => normalizeLanguage(i18n.language));
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [createModalSection, setCreateModalSection] = useState("");
  const [createForm, setCreateForm] = useState({
    label: "",
    key: "",
    type: "text",
    value: "",
    isActive: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [cardDrafts, setCardDrafts] = useState({});
  const [busyCardSections, setBusyCardSections] = useState({});
  const [cardUploadFiles, setCardUploadFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const cardKeyRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;
  const selectedPageLabel =
    contentPages.find((page) => page.key === selectedPage)?.label ||
    formatFieldTitle(selectedPage);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getAdminPageContent({ page: selectedPage, language });
      setRawData(data);
    } catch (error) {
      notify({ type: "error", title: "Failed to load content", message: error?.message || "" });
      setRawData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [language, selectedPage]);

  useEffect(() => {
    const selected = normalizeLanguage(i18n.language);
    setLanguage((prev) => (prev === selected ? prev : selected));
  }, [i18n.language]);

  const normalizedItems = useMemo(() => flattenContent(rawData), [rawData]);
  const groupedItems = useMemo(() => {
    const groups = new Map();
    let sectionPosition = 0;
    normalizedItems.forEach((item) => {
      const sectionKey = getSectionKey({
        path: item.path,
        sectionKey: item.sectionKey,
        key: item.key,
        backendSection: item.backendSection,
      });
      if (!groups.has(sectionKey)) {
        groups.set(sectionKey, { items: [], firstSeenOrder: sectionPosition++ });
      }
      groups.get(sectionKey).items.push(item);
    });

    return Array.from(groups.entries())
      .map(([sectionKey, payload]) => {
        const sectionItems = payload.items || [];
        const minId = sectionItems.reduce((min, it) => {
          const id = Number(it.id);
          if (!Number.isFinite(id)) return min;
          return Math.min(min, id);
        }, Number.POSITIVE_INFINITY);

        return {
          sectionKey,
          sectionTitle: toSectionTitle(sectionKey),
          items: [...sectionItems].sort((a, b) => (a.order || 0) - (b.order || 0)),
          firstSeenOrder: payload.firstSeenOrder,
          minId,
        };
      })
      .sort((a, b) => {
        const aHasId = Number.isFinite(a.minId);
        const bHasId = Number.isFinite(b.minId);
        if (aHasId && bHasId) return a.minId - b.minId;
        if (aHasId) return -1;
        if (bHasId) return 1;
        return a.firstSeenOrder - b.firstSeenOrder;
      })
      .map(({ firstSeenOrder, minId, ...group }) => group);
  }, [normalizedItems]);
  const [openSections, setOpenSections] = useState({});

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return groupedItems;

    return groupedItems
      .map((group) => {
        const items = group.items.filter((item) => {
          const haystack = [
            item.label,
            item.key,
            item.path,
            item.type,
            typeof item.value === "string" ? item.value : "",
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query) || group.sectionTitle.toLowerCase().includes(query);
        });
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0);
  }, [groupedItems, searchQuery]);

  useEffect(() => {
    if (!groupedItems.length) return;
    setOpenSections((prev) => {
      const next = { ...prev };
      groupedItems.forEach((group, idx) => {
        if (typeof next[group.sectionKey] === "undefined") {
          next[group.sectionKey] = idx === 0;
        }
      });
      return next;
    });
  }, [groupedItems]);

  useEffect(() => {
    if (!searchQuery.trim() || !filteredGroups.length) return;
    setOpenSections((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        next[group.sectionKey] = true;
      });
      return next;
    });
  }, [searchQuery, filteredGroups]);

  const allSectionsExpanded = useMemo(
    () =>
      filteredGroups.length > 0 &&
      filteredGroups.every((group) => openSections[group.sectionKey]),
    [filteredGroups, openSections]
  );

  const toggleAllSections = () => {
    const nextValue = !allSectionsExpanded;
    setOpenSections((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        next[group.sectionKey] = nextValue;
      });
      return next;
    });
  };

  const requireId = (item) => {
    if (!item?.id) {
      notify({ type: "error", title: "Missing item id", message: "This item cannot be edited from admin endpoint." });
      return false;
    }
    return true;
  };

  const actionWrap = async (fn, successTitle, options = { reload: true }) => {
    try {
      await fn();
      notify({ type: "success", title: successTitle });
      if (options.reload) {
        await loadData();
      }
    } catch (error) {
      notify({
        type: "error",
        title: "Action failed",
        message: error?.response?.data?.message || error?.message || "Try again.",
      });
    }
  };

  const getCardFieldMeta = (items) => {
    const cardItems = items.filter((item) => cardKeyRegex.test(String(item.key || "")));
    if (!cardItems.length) return null;

    const cardsMap = new Map();
    const suffixOrder = [];
    const suffixSeen = new Set();

    cardItems.forEach((item) => {
      const match = String(item.key || "").match(cardKeyRegex);
      if (!match) return;
      const index = Number(match[1]);
      const suffix = String(match[2] || "");
      const normalizedSuffix = suffix.toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, []);
      cardsMap.get(index).push({ suffix, normalizedSuffix, item });
      if (!suffixSeen.has(normalizedSuffix)) {
        suffixSeen.add(normalizedSuffix);
        suffixOrder.push(normalizedSuffix);
      }
    });

    const cards = Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields]) => ({
        index,
        fields: [...fields].sort(
          (a, b) =>
            suffixOrder.indexOf(a.normalizedSuffix) - suffixOrder.indexOf(b.normalizedSuffix)
        ),
      }));

    return { cards, suffixOrder, cardItems };
  };

  const toKeyFromLabel = (label) =>
    String(label || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const openCreateModal = (sectionKey) => {
    setCreateModalSection(sectionKey);
    setCreateForm({
      label: "",
      key: "",
      type: "text",
      value: "",
      isActive: true,
    });
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setCreateModalSection("");
  };

  const handleCreateSubmit = async () => {
    if (!createModalSection) return;
    const label = createForm.label.trim();
    const key = createForm.key.trim() || toKeyFromLabel(label);
    if (!label) {
      notify({ type: "error", title: "Label is required" });
      return;
    }
    if (!key) {
      notify({ type: "error", title: "Key is required", message: "Use a valid label or enter a key manually." });
      return;
    }

    const maxOrder = groupedItems
      .find((g) => g.sectionKey === createModalSection)
      ?.items.reduce((max, it) => Math.max(max, Number(it.order) || 0), 0) ?? 0;

    setIsCreating(true);
    try {
      await createContentItem({
        page: selectedPage,
        section: createModalSection,
        key,
        language,
        label,
        type: createForm.type,
        value: createForm.value,
        order: maxOrder + 1,
        isActive: createForm.isActive,
      });
      notify({ type: "success", title: "Item created" });
      setCreateModalSection("");
      await loadData();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "Try again.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className="space-y-5">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0b1f3f] via-[#122a4d] to-[#1a3a6b] p-5 sm:p-6 text-white shadow-sm">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FE7E3F]/20 blur-3xl" aria-hidden />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 mb-1">Content editor</p>
            <h2 className="text-2xl sm:text-3xl font-bold">{selectedPageLabel}</h2>
            <p className="mt-2 text-sm text-white/75 max-w-xl">{t("dashboard.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
              <Layers size={14} />
              {groupedItems.length} section{groupedItems.length === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
              <FileText size={14} />
              {normalizedItems.length} field{normalizedItems.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        {contentPages.length > 0 && onPageChange ? (
          <div className="dashboard-h-scroll relative mt-5 flex gap-2 overflow-x-auto pb-2.5 -mx-1 px-1">
            {contentPages.map((page) => {
              const isActive = page.key === selectedPage;
              return (
                <button
                  key={page.key}
                  type="button"
                  onClick={() => onPageChange(page.key)}
                  className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-white text-[#0b1f3f] shadow-sm"
                      : "bg-white/10 text-white/90 hover:bg-white/20"
                  }`}
                >
                  {page.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search fields, keys, or content..."
              className="w-full rounded-xl border border-slate-300 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#0b1f3f] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0b1f3f]/10"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {languages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(lang);
                    localStorage.setItem("irqgyp_lang", lang);
                    setLanguage(lang);
                  }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    language === lang
                      ? "bg-[#0b1f3f] text-white shadow-sm"
                      : "text-slate-600 hover:bg-white"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={toggleAllSections}
              disabled={!filteredGroups.length}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {allSectionsExpanded ? <ChevronsDownUp size={16} /> : <ChevronsUpDown size={16} />}
              {allSectionsExpanded ? "Collapse all" : "Expand all"}
            </button>

            <button
              type="button"
              onClick={loadData}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              {t("content.reload")}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
              <div className="h-5 w-40 rounded bg-slate-200" />
              <div className="mt-4 space-y-3">
                <div className="h-16 rounded-xl bg-slate-100" />
                <div className="h-16 rounded-xl bg-slate-100" />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {!loading && normalizedItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <FileText size={22} />
          </div>
          <p className="font-medium text-slate-700">{t("content.noItems")}</p>
          <p className="mt-1 text-sm text-slate-500">Try another page or language.</p>
        </div>
      ) : null}

      {!loading && normalizedItems.length > 0 && filteredGroups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
          No fields match &ldquo;{searchQuery}&rdquo;
        </div>
      ) : null}

      <div className="space-y-4">
        {filteredGroups.map((group) => (
          <section key={group.sectionKey} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {(() => {
              const cardsMeta = getCardFieldMeta(group.items);
              const nonCardItems = cardsMeta
                ? group.items.filter((item) => !cardsMeta.cardItems.some((cardItem) => cardItem.id === item.id))
                : group.items;

              const saveCard = (card) => {
                actionWrap(
                  async () => {
                    await Promise.all(
                      card.fields.map(({ item }) => {
                        const nextValue =
                          Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                            ? cardDrafts[item.id]
                            : String(item.value ?? "");
                        return updateContentValue({ id: item.id, value: nextValue });
                      })
                    );
                  },
                  `Card ${card.index} updated`
                );
              };

              const addCardFromTemplate = async () => {
                if (!cardsMeta?.cards?.length) return;
                const firstCard = cardsMeta.cards[0];
                const nextIndex = Math.max(...cardsMeta.cards.map((c) => c.index)) + 1;
                const maxOrder = group.items.reduce(
                  (max, it) => Math.max(max, Number(it.order) || 0),
                  0
                );

                setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: true }));
                try {
                  await Promise.all(
                    firstCard.fields.map(({ suffix, item }, idx) =>
                      createContentItem({
                        page: selectedPage,
                        section: group.sectionKey,
                        key: `item${nextIndex}${suffix}`,
                        language,
                        label: `${toSectionTitle(group.sectionKey)} ${formatFieldTitle(suffix)} ${nextIndex}`,
                        type: item.type || "text",
                        value: "",
                        order: maxOrder + idx + 1,
                        isActive: Boolean(item.isActive),
                      })
                    )
                  );
                  notify({ type: "success", title: `Card ${nextIndex} created` });
                  await loadData();
                } catch (error) {
                  notify({
                    type: "error",
                    title: "Create card failed",
                    message: error?.response?.data?.message || error?.message || "Try again.",
                  });
                } finally {
                  setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: false }));
                }
              };

              const isMediaField = (suffix) => {
                const normalized = String(suffix || "").toLowerCase();
                return (
                  normalized.includes("icon") ||
                  normalized.includes("image") ||
                  normalized.includes("media") ||
                  normalized.includes("video")
                );
              };

              const deleteCardCompletely = async (card) => {
                const confirmed = window.confirm(`Delete Card ${card.index} completely? This cannot be undone.`);
                if (!confirmed) return;
                setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: true }));
                try {
                  await Promise.all(
                    card.fields.map(({ item }) => deleteContentItem({ id: item.id }))
                  );
                  notify({ type: "success", title: `Card ${card.index} deleted` });
                  await loadData();
                } catch (error) {
                  notify({
                    type: "error",
                    title: "Delete card failed",
                    message: error?.response?.data?.message || error?.message || "Try again.",
                  });
                } finally {
                  setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: false }));
                }
              };

              return (
                <>
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 sm:px-5 py-3.5">
              <button
                type="button"
                onClick={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    [group.sectionKey]: !prev[group.sectionKey],
                  }))
                }
                className="flex-1 min-w-0 flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                    {group.sectionTitle}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {group.items.length} field{group.items.length === 1 ? "" : "s"}
                  </p>
                </div>
                <ChevronDown
                  size={18}
                  className={`shrink-0 text-slate-500 transition-transform ${
                    openSections[group.sectionKey] ? "rotate-180" : ""
                  }`}
                />
              </button>
              <button
                type="button"
                onClick={() => openCreateModal(group.sectionKey)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#FE7E3F] px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-[#e86f32] whitespace-nowrap"
              >
                <Plus size={14} />
                Add field
              </button>
            </div>

            <div className={`${openSections[group.sectionKey] ? "p-4 sm:p-5" : "hidden"}`}>
              {cardsMeta ? (
                <div className="mb-5 rounded-xl border border-[#0b1f3f]/10 bg-gradient-to-br from-slate-50 to-white p-4">
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <div>
                      <p className="text-sm font-semibold text-[#0b1f3f]">Repeatable cards</p>
                      <p className="text-xs text-slate-500 mt-0.5">Edit grouped card fields together</p>
                    </div>
                    <button
                      type="button"
                      onClick={addCardFromTemplate}
                      disabled={Boolean(busyCardSections[group.sectionKey])}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#0b1f3f]/20 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#0b1f3f] hover:bg-slate-50 disabled:opacity-60"
                    >
                      <Plus size={14} />
                      {busyCardSections[group.sectionKey] ? "Adding..." : "Add card"}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {cardsMeta.cards.map((card) => (
                      <div key={card.index} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-sm font-semibold text-slate-900 mb-3">Card {card.index}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {card.fields.map(({ suffix, item }) => (
                            <label key={item.id} className="text-xs text-slate-600">
                              {formatFieldTitle(suffix)}
                              <input
                                type="text"
                                value={
                                  Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                    ? cardDrafts[item.id]
                                    : String(item.value ?? "")
                                }
                                onChange={(e) =>
                                  setCardDrafts((prev) => ({ ...prev, [item.id]: e.target.value }))
                                }
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                              />
                              {isMediaField(suffix) &&
                              String(
                                Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                  ? cardDrafts[item.id]
                                  : item.value ?? ""
                              )
                                .trim()
                                .startsWith("/uploads/") ? (
                                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                                  {resolveMediaUrl(
                                    Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                      ? cardDrafts[item.id]
                                      : item.value
                                  ) ? (
                                    <img
                                      src={resolveMediaUrl(
                                        Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                          ? cardDrafts[item.id]
                                          : item.value
                                      )}
                                      alt=""
                                      className="w-full h-24 object-contain bg-white rounded border border-slate-200"
                                    />
                                  ) : (
                                    <p className="text-[11px] text-slate-500">No media uploaded</p>
                                  )}
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    <input
                                      type="file"
                                      onChange={(e) =>
                                        setCardUploadFiles((prev) => ({
                                          ...prev,
                                          [item.id]: e.target.files?.[0] || null,
                                        }))
                                      }
                                      className="w-full text-xs file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-200"
                                    />
                                    <button
                                      type="button"
                                      disabled={!cardUploadFiles[item.id] || Boolean(busyCardSections[group.sectionKey])}
                                      onClick={() =>
                                        actionWrap(
                                          () => uploadContentMedia({ id: item.id, file: cardUploadFiles[item.id] }),
                                          "Media uploaded"
                                        )
                                      }
                                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-60"
                                    >
                                      Upload
                                    </button>
                                    <button
                                      type="button"
                                      disabled={Boolean(busyCardSections[group.sectionKey])}
                                      onClick={() =>
                                        actionWrap(() => deleteContentMedia({ id: item.id }), "Media removed")
                                      }
                                      className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </label>
                          ))}
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => deleteCardCompletely(card)}
                            disabled={Boolean(busyCardSections[group.sectionKey])}
                            className="text-sm px-3 py-2 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                          >
                            Delete card
                          </button>
                          <button
                            type="button"
                            onClick={() => saveCard(card)}
                            disabled={Boolean(busyCardSections[group.sectionKey])}
                            className="text-sm px-3 py-2 rounded-lg bg-[#0b1f3f] text-white hover:bg-[#132d54] disabled:opacity-60"
                          >
                            Save card
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {nonCardItems.length > 0 ? (
                <div className="space-y-3">
                  {nonCardItems.map((item) => (
                    <EditableContentRow
                      key={`${item.path}-${item.id || item.key}`}
                      item={item}
                      t={t}
                      onSaveValue={(it, value) =>
                        requireId(it) &&
                        actionWrap(() => updateContentValue({ id: it.id, value }), "Content updated successfully")
                      }
                      onSaveOrder={(it, order) =>
                        requireId(it) &&
                        actionWrap(() => updateContentOrder({ id: it.id, order }), "Order updated")
                      }
                      onToggleActive={(it, isActive) =>
                        requireId(it) &&
                        actionWrap(() => updateContentActive({ id: it.id, isActive }), "Status updated")
                      }
                      onUploadMedia={(it, file) =>
                        requireId(it) &&
                        actionWrap(() => uploadContentMedia({ id: it.id, file }), "Media uploaded")
                      }
                      onRemoveMedia={(it) =>
                        requireId(it) && actionWrap(() => deleteContentMedia({ id: it.id }), "Media removed")
                      }
                      onDeleteItem={(it) =>
                        requireId(it) &&
                        window.confirm(`Delete "${it.label || it.key}"?`) &&
                        actionWrap(() => deleteContentItem({ id: it.id }), "Item deleted")
                      }
                    />
                  ))}
                </div>
              ) : cardsMeta ? (
                <p className="text-sm text-slate-500 text-center py-2">All fields in this section are managed as cards above.</p>
              ) : null}
            </div>
                </>
              );
            })()}
          </section>
        ))}
      </div>

      {createModalSection ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-xl rounded-2xl bg-white border border-slate-200 shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Add new field</h3>
              <p className="text-sm text-slate-500 mt-1">
                {toSectionTitle(createModalSection)} · {language.toUpperCase()}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <label className="block text-sm text-slate-700">
                Label
                <input
                  type="text"
                  value={createForm.label}
                  onChange={(e) => {
                    const nextLabel = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      label: nextLabel,
                      key: prev.key || toKeyFromLabel(nextLabel),
                    }));
                  }}
                  placeholder="Example: Hero Title"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Key
                <input
                  type="text"
                  value={createForm.key}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, key: e.target.value }))}
                  placeholder="Auto generated from label"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Type
                <select
                  value={createForm.type}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, type: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="richtext">Rich text</option>
                  <option value="link">Link</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </label>

              <label className="block text-sm text-slate-700">
                Initial Value
                <textarea
                  value={createForm.value}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, value: e.target.value }))}
                  placeholder="Enter content..."
                  className="mt-1 w-full min-h-[110px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={createForm.isActive}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCreateModal}
                disabled={isCreating}
                className="text-sm px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSubmit}
                disabled={isCreating}
                className="text-sm px-4 py-2 rounded-lg bg-[#FE7E3F] font-medium text-white hover:bg-[#e86f32] disabled:opacity-60"
              >
                {isCreating ? "Creating..." : "Create field"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ContentManager;
